(() => {
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const heroDefinitions = [
    {
      slide: '.slideshow-slide-einsatzbereiche',
      copy: '.echtools-area-left',
      heading: '.slideshow-heading',
      list: '.echtools-area-list',
      products: '.echtools-area-cards',
      label: 'Einsatzbereiche'
    },
    {
      slide: '.slideshow-slide-werkzeuge',
      copy: '.echtools-werkzeuge-copy',
      heading: '.slideshow-heading',
      list: '.echtools-werkzeuge-list',
      products: '.echtools-werkzeuge-products',
      label: 'Werkzeuggruppen'
    },
    {
      slide: '.slideshow-slide-fahrzeug-waehlen',
      copy: '.fahrzeug-hero-copy',
      heading: '.fahrzeug-hero-heading',
      list: '.fahrzeug-hero-brands',
      products: '.fahrzeug-hero-products',
      label: 'Fahrzeugmarken'
    }
  ];
  const productLinkSelector = [
    '.echtools-area-card',
    '.echtools-werkzeuge-product',
    '.fahrzeug-hero-product'
  ].join(',');
  const productLinkAttributes = ['href', 'target', 'rel', 'download', 'ping', 'referrerpolicy', 'tabindex', 'aria-disabled'];
  const originalProductLinkAttributes = new WeakMap();
  let openTimer = null;
  let userPanelPreference = null;
  let activeSlideshow = null;

  const syncProductLinkMode = (scope = document) => {
    const cards = [];
    if (scope.matches?.(productLinkSelector)) cards.push(scope);
    cards.push(...scope.querySelectorAll(productLinkSelector));

    cards.forEach((card) => {
      if (mobileQuery.matches) {
        if (!originalProductLinkAttributes.has(card)) {
          originalProductLinkAttributes.set(card, new Map(
            productLinkAttributes.map((attribute) => [attribute, card.getAttribute(attribute)])
          ));
        }
        ['href', 'target', 'rel', 'download', 'ping', 'referrerpolicy'].forEach((attribute) => card.removeAttribute(attribute));
        card.setAttribute('tabindex', '-1');
        card.setAttribute('aria-disabled', 'true');
        return;
      }

      const originalAttributes = originalProductLinkAttributes.get(card);
      if (!originalAttributes) return;
      originalAttributes.forEach((value, attribute) => {
        if (value === null) card.removeAttribute(attribute);
        else card.setAttribute(attribute, value);
      });
      originalProductLinkAttributes.delete(card);
    });
  };

  const clearOpenTimer = () => {
    if (openTimer) window.clearTimeout(openTimer);
    openTimer = null;
  };

  const setPanelOpen = (state, open) => {
    const { slide, list, toggle, products } = state;
    slide.classList.toggle('et-mobile-categories-open', open);
    list.setAttribute('aria-hidden', String(!open));
    list.inert = !open;
    products.inert = open;
    toggle.setAttribute('aria-expanded', String(open));
    toggle.textContent = open ? 'Produkte ansehen' : 'Kategorien ansehen';
    toggle.hidden = !mobileQuery.matches;
  };

  const setDesktopState = (states) => {
    states.forEach((state) => {
      state.slide.classList.remove(
        'et-mobile-categories-open',
        'et-mobile-universal-selected',
        'et-mobile-family-arretier',
        'et-mobile-family-thread',
        'et-mobile-family-hydraulic',
        'et-mobile-family-workshop'
      );
      state.list.setAttribute('aria-hidden', 'false');
      state.list.inert = false;
      state.products.inert = false;
      state.toggle.hidden = true;
    });
  };

  const existingPanelStates = (slideshow) => heroDefinitions.flatMap((definition) => {
    const slide = slideshow.querySelector(definition.slide);
    const list = slide?.querySelector('.et-mobile-category-panel');
    const products = slide?.querySelector(definition.products);
    const toggle = slide?.querySelector('.et-mobile-category-toggle');
    return slide && list && products && toggle ? [{ definition, slide, list, products, toggle }] : [];
  });

  const selectedProductSlide = (slideshow) => {
    const selected = slideshow.querySelector('.slideshow-slide.is-selected');
    if (selected) return selected;
    return slideshow.querySelector(heroDefinitions.map((item) => item.slide).join(','));
  };

  const activateSelectedSlide = (slideshow, states, autoOpen = true) => {
    clearOpenTimer();
    states.forEach((state) => setPanelOpen(state, false));

    if (!mobileQuery.matches) {
      setDesktopState(states);
      return;
    }

    const selectedSlide = selectedProductSlide(slideshow);
    const selectedState = states.find((state) => state.slide === selectedSlide);
    if (!selectedState) return;

    if (userPanelPreference !== null) {
      setPanelOpen(selectedState, userPanelPreference);
      return;
    }

    if (!autoOpen) return;

    openTimer = window.setTimeout(() => {
      const currentSlide = selectedProductSlide(slideshow);
      if (mobileQuery.matches && currentSlide === selectedState.slide) {
        setPanelOpen(selectedState, true);
      }
      openTimer = null;
    }, 900);
  };

  const makeToggle = (state, id) => {
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'et-mobile-category-toggle';
    toggle.setAttribute('aria-controls', id);
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', `Mobile Ansicht: ${state.definition.label}`);
    toggle.textContent = 'Kategorien ansehen';
    toggle.hidden = !mobileQuery.matches;
    state.copy.insertBefore(toggle, state.list);
    state.toggle = toggle;

    toggle.addEventListener('click', () => {
      clearOpenTimer();
      userPanelPreference = !state.slide.classList.contains('et-mobile-categories-open');
      setPanelOpen(state, userPanelPreference);
    });
  };

  const initSlideshow = (root) => {
    const slideshow = root.querySelector('.slideshow');
    if (!slideshow) return;
    if (root.dataset.etMobileHeroPanelsReady === 'true') {
      if (!mobileQuery.matches) setDesktopState(existingPanelStates(slideshow));
      return;
    }

    const states = heroDefinitions.flatMap((definition, index) => {
      const slide = slideshow.querySelector(definition.slide);
      const copy = slide?.querySelector(definition.copy);
      const heading = copy?.querySelector(definition.heading);
      const list = copy?.querySelector(definition.list);
      const products = slide?.querySelector(definition.products);
      if (!slide || !copy || !heading || !list || !products) return [];

      list.id ||= `${(slideshow.id || 'home-hero').replace(/[^a-z0-9_-]/gi, '')}-mobile-categories-${index + 1}`;
      list.classList.add('et-mobile-category-panel');
      const state = { definition, slide, copy, list, products, toggle: null };
      makeToggle(state, list.id);
      setPanelOpen(state, false);

      slide.addEventListener('click', (event) => {
        if (!mobileQuery.matches) return;

        const toolCategory = event.target.closest('.echtools-werkzeuge-category');
        if (toolCategory && slide.contains(toolCategory)) {
          clearOpenTimer();
          userPanelPreference = true;
          slide.classList.remove(
            'et-mobile-family-arretier',
            'et-mobile-family-thread',
            'et-mobile-family-hydraulic',
            'et-mobile-family-workshop'
          );
          if (['arretier', 'thread', 'hydraulic', 'workshop'].includes(toolCategory.dataset.family)) {
            slide.classList.add(`et-mobile-family-${toolCategory.dataset.family}`);
          }
          setPanelOpen(state, true);
          return;
        }

        const vehicleChoice = event.target.closest('.fahrzeug-hero-brands button[data-sku]');
        if (vehicleChoice && slide.contains(vehicleChoice)) {
          clearOpenTimer();
          userPanelPreference = false;
          slide.classList.toggle('et-mobile-universal-selected', vehicleChoice.dataset.sku === 'kt6387');
          setPanelOpen(state, false);
          window.requestAnimationFrame(() => {
            slide.querySelector(`.fahrzeug-hero-product[data-sku="${vehicleChoice.dataset.sku}"]`)?.focus({ preventScroll: true });
          });
          return;
        }

        if (event.target.closest(productLinkSelector)) {
          clearOpenTimer();
          userPanelPreference = false;
          setPanelOpen(state, false);
        }
      });

      slide.addEventListener('focusin', (event) => {
        if (!event.target.closest(productLinkSelector)) return;
        clearOpenTimer();
        userPanelPreference = false;
        setPanelOpen(state, false);
      });

      slide.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !slide.classList.contains('et-mobile-categories-open')) return;
        userPanelPreference = false;
        setPanelOpen(state, false);
        toggle.focus({ preventScroll: true });
      });

      return [state];
    });

    if (!states.length) return;
    root.dataset.etMobileHeroPanelsReady = 'true';
    activeSlideshow = slideshow;

    let previousSelectedSlide = selectedProductSlide(slideshow);
    document.addEventListener('slideshow:slide-change', (event) => {
      if (event.detail?.id !== slideshow.id) return;
      const currentSelectedSlide = selectedProductSlide(slideshow);
      const changedSlide = currentSelectedSlide !== previousSelectedSlide;
      previousSelectedSlide = currentSelectedSlide;
      activateSelectedSlide(slideshow, states, changedSlide);
    });

    activateSelectedSlide(slideshow, states, false);
  };

  const fitMobileHero = (root, force = false) => {
    const slideshow = root.querySelector('.slideshow');
    if (!slideshow || !slideshow.querySelector(heroDefinitions.map((item) => item.slide).join(','))) return;

    if (!mobileQuery.matches) {
      slideshow.style.removeProperty('--et-mobile-hero-height');
      delete slideshow.dataset.etMobileViewportWidth;
      return;
    }

    const viewportWidth = Math.round(window.visualViewport?.width || window.innerWidth);
    const previousWidth = Number(slideshow.dataset.etMobileViewportWidth || 0);
    if (!force && previousWidth && Math.abs(viewportWidth - previousWidth) < 10) return;

    const viewportHeight = Math.round(window.visualViewport?.height || window.innerHeight);
    const documentTop = Math.max(0, Math.round(slideshow.getBoundingClientRect().top + window.scrollY));
    const availableHeight = Math.max(36 * parseFloat(getComputedStyle(document.documentElement).fontSize), viewportHeight - documentTop);
    slideshow.style.setProperty('--et-mobile-hero-height', `${Math.round(availableHeight)}px`);
    slideshow.dataset.etMobileViewportWidth = String(viewportWidth);
  };

  const init = (scope = document) => {
    syncProductLinkMode(scope);
    scope.querySelectorAll('slideshow-component').forEach((root) => {
      initSlideshow(root);
      fitMobileHero(root, true);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();

  window.addEventListener('pageshow', () => init());
  window.addEventListener('resize', () => init());
  window.addEventListener('orientationchange', () => window.setTimeout(() => init(), 180), { passive: true });
  mobileQuery.addEventListener?.('change', () => {
    userPanelPreference = null;
    syncProductLinkMode(document);
    document.querySelectorAll('slideshow-component').forEach((root) => {
      const slideshow = root.querySelector('.slideshow');
      if (!slideshow) return;
      const states = existingPanelStates(slideshow);
      if (!mobileQuery.matches) setDesktopState(states);
      else if (activeSlideshow === slideshow) activateSelectedSlide(slideshow, states);
      fitMobileHero(root, true);
    });
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
